import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { COMPANY, IMAGES } from "@/data";

const LINES = ["Glas so klar,", "als wäre es", "nicht da."];

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section id="start" ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-28">
        <motion.div style={reduce ? undefined : { opacity: fade }} className="lg:col-span-7">
          <div
            data-testid="hero-availability-pill"
            className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-200/80 bg-emerald-50/80 px-4 py-1.5"
          >
            <span className="animate-pulse-dot h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-mono text-xs font-medium uppercase tracking-[0.14em] text-emerald-800">
              Termine in Ihrer Region diese Woche verfügbar
            </span>
          </div>

          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: reduce ? 0 : "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 2 ? (
                    <>
                      <em className="italic text-sky-700">{line}</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Professionelle Fenster- und Glasreinigung für Privat- und Gewerbekunden in{" "}
            {COMPANY.region}. Streifenfrei garantiert – zum verbindlichen Festpreis.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#kontakt"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-700 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(2,132,199,0.5)] transition-all duration-300 hover:bg-sky-800 hover:shadow-[0_14px_40px_-10px_rgba(2,132,199,0.6)]"
            >
              Kostenloses Angebot anfordern
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href={COMPANY.phoneHref}
              data-testid="hero-phone-cta"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/70 px-7 py-3.5 text-sm font-semibold text-slate-800 backdrop-blur transition-colors duration-300 hover:border-sky-400 hover:text-sky-700"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            data-testid="hero-trust-stamps"
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-slate-200 pt-6"
          >
            {[
              { icon: ShieldCheck, label: "100% Streifenfrei-Garantie" },
              { icon: BadgeCheck, label: "Inhabergeführt" },
              { icon: Sparkles, label: "Vollversichert" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <Icon className="h-4 w-4 text-sky-700" />
                {label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: reduce ? 0 : 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
        >
          <div className="relative overflow-hidden rounded-2xl shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
            <div className="aspect-[4/5] overflow-hidden">
              <motion.img
                src={IMAGES.hero}
                alt="Modernes Einfamilienhaus mit großen, sauberen Fenstern"
                style={reduce ? undefined : { y: imgY }}
                className="h-[116%] w-full scale-110 object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-xl border border-white/30 bg-white/80 px-5 py-4 backdrop-blur-md">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Ihr Ansprechpartner</p>
                <p className="font-serif text-lg font-medium text-slate-900">{COMPANY.owner}</p>
              </div>
              <span className="rounded-full bg-sky-700 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                {COMPANY.founded}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
